import React from 'react';
import {
  PLANNER_TIME_FORMAT_12,
  PLANNER_TIME_FORMAT_24,
} from '../../moduleServices/planner_time_format';

/**
 * Fade past tasks, month calendar density, clock format — single settings card.
 */
const CardGeneralOptions = ({
  fadePastEvents = true,
  onFadePastEventsChange,
  weekRowsVisible = 6,
  onWeekRowsVisibleChange,
  /** @type {'24h' | '12h'} */
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
  onTimeFormatPreferenceChange,
}) => {
  return (
    <div className="rounded-xl bg-white p-4 shadow">
      <h3 className="mb-4 text-lg font-semibold text-gray-800">General Options</h3>

      <ul className="divide-y divide-gray-100 border-t border-gray-100">
        {/* Fade past */}
        <li className="flex flex-wrap items-center gap-3 py-3">
          <button
            type="button"
            role="switch"
            aria-checked={fadePastEvents}
            onClick={() => onFadePastEventsChange?.(!fadePastEvents)}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
              fadePastEvents ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                fadePastEvents ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
          <p className="min-w-0 flex-1 text-sm text-gray-600">
            <span className="font-medium text-gray-800">Fade past tasks &amp; events · </span>
            past items appear softer on the Events tab.
          </p>
        </li>

        {/* Month week rows */}
        <li className="flex flex-wrap items-center gap-3 py-3">
          <div
            className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-0.5"
            role="group"
            aria-label="Visible week rows in month calendar"
          >
            <button
              type="button"
              role="radio"
              aria-checked={weekRowsVisible === 6}
              onClick={() => onWeekRowsVisibleChange?.(6)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                weekRowsVisible === 6
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              6 rows
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={weekRowsVisible === 5}
              onClick={() => onWeekRowsVisibleChange?.(5)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                weekRowsVisible === 5
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              5 rows
            </button>
          </div>
          <p className="min-w-0 flex-1 text-sm text-gray-600">
            <span className="font-medium text-gray-800">Monthly calendar · </span>
            how many week bands fill the view (fewer rows = taller cells).
          </p>
        </li>

        {/* Clock format */}
        <li className="flex flex-wrap items-center gap-3 py-3">
          <div
            className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-0.5"
            role="group"
            aria-label="Time display format"
          >
            <button
              type="button"
              role="radio"
              aria-checked={timeFormatPreference === PLANNER_TIME_FORMAT_24}
              onClick={() => onTimeFormatPreferenceChange?.(PLANNER_TIME_FORMAT_24)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium tabular-nums transition-colors ${
                timeFormatPreference === PLANNER_TIME_FORMAT_24
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              24h
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={timeFormatPreference === PLANNER_TIME_FORMAT_12}
              onClick={() => onTimeFormatPreferenceChange?.(PLANNER_TIME_FORMAT_12)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                timeFormatPreference === PLANNER_TIME_FORMAT_12
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              AM/PM
            </button>
          </div>
          <p className="min-w-0 flex-1 text-sm text-gray-600">
            <span className="font-medium text-gray-800">Clock format · </span>
            chips, picker, hover, and event list use this style.
          </p>
        </li>
      </ul>
    </div>
  );
};

export default CardGeneralOptions;
